import 'azure-maps-control/dist/atlas.min.css';
import * as atlas from 'azure-maps-control';
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { AzureMap, AzureMapDataSourceProvider, AzureMapLayerProvider, AzureMapPopup, AzureMapsContext, AzureMapsProvider } from 'react-azure-maps';
import MapComponentProps from '../../../types/MapModels';
import MapQuerier from '../../../utilities/MapQuerier';
import { AzureMapCameraControl } from './AzureMapCameraControl';
import { SYMBOL_MARKER_OFFSET_EXPRESSION } from './AzureMapMarkerConstants';
import { AzureMapSpriteRegistrar } from './AzureMapSpriteRegistrar';
import { DEFAULT_CAMERA_PADDING } from './AzureMapUtils';
import InteractivePopup from './InteractivePopup';
import { MapPopupContent } from './MapPopupContent';
import { convertInfoboxesToAzure, convertMapComponentPropsToAzure, convertPolygonsToAzure, convertPolylinesToAzure, convertPushpinsToAzure } from './MapToAzureMapAdapter';
import { useAzureMapCamera } from './useAzureMapCamera';

type Props = MapComponentProps & { children?: React.ReactNode };

const MapRefBinder: React.FC<{ onReady: (map: atlas.Map | null) => void; }> = ({ onReady }) => {
    const { mapRef, isMapReady } = React.useContext(AzureMapsContext);
    useEffect(() => {
        if (isMapReady && mapRef) {
            onReady(mapRef);
        }
    }, [isMapReady, mapRef, onReady]);
    return null;
};

const TransitMap = React.forwardRef<MapQuerier, Props>(function TransitMap(props, ref) {
    const {
        map: mapProps,
        pushpins = [],
        polylines = [],
        polygons = [],
        infoboxes = [],
        children,
    } = props;

    const [pushpinState, setPushpinState] = useState(pushpins);
    const [polylineState, setPolylineState] = useState(polylines);
    const [polygonState, setPolygonState] = useState(polygons);
    const [infoboxState, setInfoboxState] = useState(infoboxes);
    const mapInstance = useRef<atlas.Map | null>(null);

    useEffect(() => {
        if (JSON.stringify(pushpins) !== JSON.stringify(pushpinState)) {
            setPushpinState(pushpins);
        }
    }, [pushpins]);
    useEffect(() => {
        if (JSON.stringify(polylines) !== JSON.stringify(polylineState)) {
            setPolylineState(polylines);
        }
    }, [polylines]);
    useEffect(() => {
        if (JSON.stringify(polygons) !== JSON.stringify(polygonState)) {
            setPolygonState(polygons);
        }
    }, [polygons]);
    useEffect(() => {
        if (JSON.stringify(infoboxes) !== JSON.stringify(infoboxState)) {
            setInfoboxState(infoboxes);
        }
    }, [infoboxes]);

    const mapOptions = useMemo(() => convertMapComponentPropsToAzure({
        map: mapProps,
        pushpins: pushpinState,
        polylines: polylineState,
        polygons: polygonState,
        infoboxes: infoboxState,
    }).mapOptions, [mapProps, pushpinState, polylineState, polygonState, infoboxState]);

    const mapEvents = useMemo(() => convertMapComponentPropsToAzure({
        map: mapProps,
        pushpins: pushpinState,
        polylines: polylineState,
        polygons: polygonState,
        infoboxes: infoboxState,
    }).mapEvents, [mapProps, pushpinState, polylineState, polygonState, infoboxState]);

    const polylineFeatures = useMemo(() => convertPolylinesToAzure(polylineState), [polylineState]);
    const polygonFeatures = useMemo(() => convertPolygonsToAzure(polygonState), [polygonState]);
    const pushpinFeatures = useMemo(() => convertPushpinsToAzure(pushpinState), [pushpinState]);
    const popupFeatures = useMemo(() => convertInfoboxesToAzure(infoboxState), [infoboxState]);

    const autoBounds = useAzureMapCamera({
        pushpinsProp: pushpinState,
        polylinesProp: polylineState,
        disableAutoCamera: mapProps?.options?.disableAutoCamera === true,
    });
    const explicitBounds = mapProps?.options?.bounds;

    useImperativeHandle(ref, () => new MapQuerier(
        (mapInstance.current as unknown as any),
        setPushpinState,
    ), [mapInstance, setPushpinState]);

    useEffect(() => {
        if (!mapInstance.current || !mapEvents) return;
        const eventEntries = Object.entries(mapEvents);
        eventEntries.forEach(([eventName, handler]) => {
            mapInstance.current!.events.add(eventName as any, handler as any);
        });
        
        // Add click handler for the map itself to support Trip Planner
        const mapClickHandler = (e: atlas.MapMouseEvent) => {
            if (mapEvents.click) {
                const map = mapInstance.current;
                const pixel = e.pixel || (e.originalEvent && [ (e.originalEvent as MouseEvent).offsetX, (e.originalEvent as MouseEvent).offsetY ]);
                
                const proxyMap = {
                    tryPixelToLocation: (p: [number, number]) => {
                        const pos = map?.pixelsToPositions([p]);
                        if (pos && pos.length > 0) {
                            return {
                                latitude: pos[0][1],
                                longitude: pos[0][0],
                            };
                        }
                        return null;
                    },
                };
                
                mapEvents.click({
                    ...e,
                    target: proxyMap,
                    getX: () => pixel ? pixel[0] : 0,
                    getY: () => pixel ? pixel[1] : 0,
                });
            }
        };
        mapInstance.current.events.add('click', mapClickHandler as any);

        return () => {
            eventEntries.forEach(([eventName, handler]) => {
                mapInstance.current?.events.remove(eventName as any, handler as any);
            });
            mapInstance.current?.events.remove('click', mapClickHandler as any);
        };
    }, [mapEvents]);

    const popups = useMemo(() => popupFeatures
        ?.filter(infobox => infobox.visible !== false)
        .filter(infobox => infobox.htmlContent || infobox.content || infobox.options.description || infobox.title || infobox.options.title)
        .map((infobox, idx) => {
            const popupOptions = {
                position: infobox.options.position,
                closeButton: infobox.options.closeButton ?? false,
                pixelOffset: infobox.htmlContent ? [0, 0] : (infobox.options.pixelOffset ?? [0, -20]),
            };

            if (infobox.htmlContent) {
                return (
                    <AzureMapPopup
                        key={`popup-${idx}`}
                        isVisible={true}
                        options={popupOptions}
                        popupContent={<div className="popup-html-container" dangerouslySetInnerHTML={{ __html: infobox.htmlContent }} />}
                    />
                );
            }

            return (
                <InteractivePopup
                    key={`popup-${idx}`}
                    isVisible={true}
                    options={popupOptions as any}
                >
                    <MapPopupContent
                        title={infobox.title ?? infobox.options.title}
                        text={infobox.content || infobox.options.description || ''}
                        accentColor={infobox.options.popupAccentColor}
                    />
                </InteractivePopup>
            );
        }), [popupFeatures]);

    const pushpinLayerEvents = useMemo(() => ({
        click: (e: any) => {
            const shape = e?.shapes?.[0];
            const props = shape?.getProperties ? shape.getProperties() : shape?.properties || {};
            const cb = props?.onClick ?? props?.onclick;
            if (cb) cb(e);
        },
        mouseover: (e: any) => {
            const shape = e?.shapes?.[0];
            const props = shape?.getProperties ? shape.getProperties() : shape?.properties || {};
            const cb = props?.onmouseover;
            if (cb) cb(e);
        },
        mouseout: (e: any) => {
            const shape = e?.shapes?.[0];
            const props = shape?.getProperties ? shape.getProperties() : shape?.properties || {};
            const cb = props?.onmouseout;
            if (cb) cb(e);
        },
    }), []);

    return (
        <div style={{ width: '100%', height: '100%' }} {...props.div}>
            <AzureMapsProvider>
                <AzureMap options={mapOptions}>
                    <MapRefBinder onReady={(map) => { mapInstance.current = map; }} />
                    <AzureMapCameraControl
                        bounds={explicitBounds ?? autoBounds}
                        padding={DEFAULT_CAMERA_PADDING}
                        disabled={mapProps?.options?.disableAutoCamera === true && !explicitBounds}
                    />
                    <AzureMapSpriteRegistrar />

                    <AzureMapDataSourceProvider id="polylineSource" collection={polylineFeatures}>
                        <AzureMapLayerProvider id="polylineLayer" type="LineLayer" options={{
                            strokeColor: ['coalesce', ['get', 'strokeColor'], '#026db5'] as any,
                            strokeWidth: ['coalesce', ['get', 'strokeWidth'], 3] as any,
                        }} />
                    </AzureMapDataSourceProvider>

                    <AzureMapDataSourceProvider id="polygonSource" collection={polygonFeatures}>
                        <AzureMapLayerProvider id="polygonLayer" type="PolygonLayer" options={{
                            fillColor: ['coalesce', ['get', 'fillColor'], '#248bd8'] as any,
                            strokeColor: ['coalesce', ['get', 'strokeColor'], '#026db5'] as any,
                            strokeWidth: ['coalesce', ['get', 'strokeWidth'], 2] as any,
                        }} />
                    </AzureMapDataSourceProvider>

                    <AzureMapDataSourceProvider id="pushpinSource" collection={pushpinFeatures}>
                        <AzureMapLayerProvider
                            id="pushpinBubbleLayer"
                            type="BubbleLayer"
                            options={{
                                filter: ['==', ['coalesce', ['get', 'renderAsBubble'], false], true] as any,
                                radius: ['coalesce', ['get', 'radius'], 6] as any,
                                color: ['coalesce', ['get', 'color'], '#2C5DAB'] as any,
                                strokeColor: ['coalesce', ['get', 'strokeColor'], '#ffffff'] as any,
                                strokeWidth: ['coalesce', ['get', 'strokeWidth'], 2] as any,
                            }}
                            events={pushpinLayerEvents as any}
                        />
                        <AzureMapLayerProvider
                            id="pushpinLayer"
                            type="SymbolLayer"
                            options={{
                                filter: ['!=', ['coalesce', ['get', 'renderAsBubble'], false], true] as any,
                                iconOptions: {
                                    ignorePlacement: true,
                                    iconAllowOverlap: true,
                                    image: ['coalesce', ['get', 'icon'], 'pin-round-blue'] as any,
                                    offset: SYMBOL_MARKER_OFFSET_EXPRESSION,
                                    anchor: 'center',
                                },
                                textOptions: {
                                    textField: ['get', 'title'],
                                },
                            }}
                            events={pushpinLayerEvents as any}
                        />
                    </AzureMapDataSourceProvider>

                    {popups as any}
                    {children as any}
                </AzureMap>
            </AzureMapsProvider>
        </div>
    );
});

export default TransitMap;
